import type { Appointment } from '../types';

// Mirrors functions/src/payments/deposit.ts, which decides the actual charge.
// Keep the two in sync; this copy only drives what the booking UI shows.

export type PaymentDepositType = 'full' | 'percent' | 'fixed';

export type PaymentDeposit = {
  type: PaymentDepositType;
  percent?: number;
  fixedAmountCents?: number;
};

// Stripe rejects USD charges below $0.50.
export const MIN_CHARGE_CENTS = 50;
export const MAX_DEPOSIT_PERCENT = 99;
export const MAX_FIXED_DEPOSIT_CENTS = 1000000;

export const FULL_PAYMENT: PaymentDeposit = { type: 'full' };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isValidPaymentDeposit = (value: unknown): value is PaymentDeposit => {
  if (!isRecord(value)) return false;
  if (value.type === 'full') return true;
  if (value.type === 'percent') {
    return Number.isInteger(value.percent) &&
      Number(value.percent) >= 1 &&
      Number(value.percent) <= MAX_DEPOSIT_PERCENT;
  }
  if (value.type === 'fixed') {
    return Number.isInteger(value.fixedAmountCents) &&
      Number(value.fixedAmountCents) >= MIN_CHARGE_CENTS &&
      Number(value.fixedAmountCents) <= MAX_FIXED_DEPOSIT_CENTS;
  }
  return false;
};

// Missing or malformed settings mean full payment.
export const resolvePaymentDeposit = (value: unknown): PaymentDeposit => {
  if (!isValidPaymentDeposit(value)) return FULL_PAYMENT;
  if (value.type === 'percent') return { type: 'percent', percent: value.percent };
  if (value.type === 'fixed') return { type: 'fixed', fixedAmountCents: value.fixedAmountCents };
  return FULL_PAYMENT;
};

export type BookingChargePlan = {
  paymentType: 'deposit' | 'full';
  chargeCents: number;
  depositAmountCents: number | null;
  // null when the final price is not known yet ("Price varies" bookings).
  balanceDueCents: number | null;
};

// What a booking charges online before any tip, or null when it is paid at the visit.
export const planBookingCharge = ({
  serviceTotalCents,
  priceVaries,
  paymentRequired,
  deposit,
  payInFull,
}: {
  serviceTotalCents: number;
  priceVaries: boolean;
  paymentRequired: boolean;
  deposit: PaymentDeposit;
  payInFull: boolean;
}): BookingChargePlan | null => {
  const depositApplies = paymentRequired && deposit.type !== 'full' && !payInFull;

  if (priceVaries) {
    if (depositApplies && deposit.type === 'fixed' && deposit.fixedAmountCents) {
      return {
        paymentType: 'deposit',
        chargeCents: deposit.fixedAmountCents,
        depositAmountCents: deposit.fixedAmountCents,
        balanceDueCents: null,
      };
    }
    return null;
  }

  const full: BookingChargePlan = {
    paymentType: 'full',
    chargeCents: serviceTotalCents,
    depositAmountCents: null,
    balanceDueCents: 0,
  };
  if (!depositApplies) return full;

  const depositCents = deposit.type === 'percent'
    ? Math.max(MIN_CHARGE_CENTS, Math.round(serviceTotalCents * Number(deposit.percent) / 100))
    : Number(deposit.fixedAmountCents);
  if (depositCents >= serviceTotalCents) return full;

  return {
    paymentType: 'deposit',
    chargeCents: depositCents,
    depositAmountCents: depositCents,
    balanceDueCents: serviceTotalCents - depositCents,
  };
};

export const describePaymentDeposit = (deposit: PaymentDeposit): string => {
  if (deposit.type === 'percent') return `${deposit.percent}% deposit`;
  if (deposit.type === 'fixed') return `$${(Number(deposit.fixedAmountCents) / 100).toFixed(2)} deposit`;
  return 'Full price';
};

export type AppointmentDepositSummary = {
  depositPaid: number;
  // null while staff have not set the final price.
  balanceDue: number | null;
};

// For a booking paid by deposit, what was paid and what is still owed. The
// balance follows billingAmount, so it updates once staff set a final price.
export const appointmentDepositSummary = (
  appointment: Pick<Appointment, 'billingAmount' | 'priceToConfirm' | 'paid' | 'paymentInfo'>
): AppointmentDepositSummary | null => {
  const info = appointment.paymentInfo;
  if (!appointment.paid || info?.paymentType !== 'deposit' || typeof info.depositAmountCents !== 'number') {
    return null;
  }
  const depositCents = info.depositAmountCents;
  const balanceDue = appointment.priceToConfirm === true
    ? null
    : Math.max(0, Math.round((appointment.billingAmount ?? 0) * 100) - depositCents) / 100;
  return { depositPaid: depositCents / 100, balanceDue };
};
